import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Activity, Droplets, Layers3, Plus, Sprout, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type {
  InputCatalogueItem,
  InventoryLocation,
  NurseryActivity,
  NurseryActivityDefinition,
  NurseryPopulationAssessment,
  NurserySeedlingLot,
  NurseryVariety,
  StaffDetail,
  VarietyDetailStatus,
} from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type Section = "ASSESSMENT" | "ACTIVITY" | "VARIETY" | undefined;
type VarietyLine = { varietyId: string; quantity: string; notes: string };

const number = new Intl.NumberFormat("en-UG");

export default function NurseryLotDetailPage() {
  const { lotId = "" } = useParams();
  const [lot, setLot] = useState<NurserySeedlingLot>();
  const [assessments, setAssessments] = useState<NurseryPopulationAssessment[]>([]);
  const [activities, setActivities] = useState<NurseryActivity[]>([]);
  const [activityCatalogue, setActivityCatalogue] = useState<NurseryActivityDefinition[]>([]);
  const [varieties, setVarieties] = useState<NurseryVariety[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InputCatalogueItem[]>([]);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [section, setSection] = useState<Section>();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  const [assessment, setAssessment] = useState({
    assessedAt: new Date().toISOString().slice(0, 16),
    currentViableQuantity: "",
    readyQuantity: "",
    mortalitySinceLast: "",
    culledSinceLast: "",
    varietyDetailStatus: "NOT_RECORDED" as VarietyDetailStatus,
    notes: "",
    assessedByEmployeeId: "",
  });
  const [activity, setActivity] = useState({
    activityId: "",
    performedAt: new Date().toISOString().slice(0, 16),
    responsibleEmployeeId: "",
    notes: "",
    inputItemId: "",
    inventoryLocationId: "",
    inputQuantity: "",
    inputUnit: "",
  });
  const [varietyStatus, setVarietyStatus] = useState<VarietyDetailStatus>("NOT_RECORDED");
  const [varietyLines, setVarietyLines] = useState<VarietyLine[]>([]);

  const load = useCallback(async () => {
    try {
      const [lotRecord, assessmentRows, activityRows, activityDefs, varietyRows, staffResult, catalogueResult, locationResult] = await Promise.all([
        services.nursery.getLot(lotId),
        services.nursery.listPopulationAssessments(lotId),
        services.nursery.listActivities(undefined, lotId),
        services.nursery.listActivityCatalogue(),
        services.nursery.listVarieties(),
        services.staff.list({ pageSize: 100 }),
        services.inputs.listCatalogue({ pageSize: 100 }),
        services.inventory.listLocations({ pageSize: 100 }),
      ]);
      if (!lotRecord) throw new Error("Nursery seedling lot was not found.");
      setLot(lotRecord);
      setAssessments(assessmentRows);
      setActivities(activityRows);
      setActivityCatalogue(activityDefs);
      setVarieties(varietyRows);
      setStaff(staffResult.items.filter((member) => member.employmentStatus === "ACTIVE"));
      setInventoryItems(catalogueResult.items.filter((item) => item.stockTrackingMode === "INVENTORY" && item.status === "ACTIVE"));
      setLocations(locationResult.items.filter((location) => location.status === "ACTIVE"));
      setAssessment((current) => ({
        ...current,
        currentViableQuantity: String(lotRecord.currentQuantity),
        readyQuantity: String(lotRecord.readyQuantity),
        varietyDetailStatus: lotRecord.varietyDetailStatus,
      }));
      setVarietyStatus(lotRecord.varietyDetailStatus);
      setVarietyLines(lotRecord.varieties.map((row) => ({
        varietyId: row.varietyId,
        quantity: row.estimatedQuantity === undefined ? "" : String(row.estimatedQuantity),
        notes: row.notes || "",
      })));
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Seedling lot could not be loaded.");
    }
  }, [lotId]);

  useEffect(() => { void load(); }, [load]);

  const selectedActivity = useMemo(
    () => activityCatalogue.find((item) => item.id === activity.activityId),
    [activityCatalogue, activity.activityId],
  );

  const currentVarietyTotal = useMemo(
    () => varietyLines.reduce((sum, row) => sum + (row.quantity ? Number(row.quantity) : 0), 0),
    [varietyLines],
  );

  const addVarietyLine = () => {
    const next = varieties.find((candidate) => !varietyLines.some((line) => line.varietyId === candidate.id));
    if (!next) {
      toast("All registered varieties are already listed.");
      return;
    }
    setVarietyLines((current) => [...current, { varietyId: next.id, quantity: "", notes: "" }]);
  };

  const saveVarieties = async (event: FormEvent) => {
    event.preventDefault();
    if (!lot) return;
    if (varietyStatus === "COMPLETE") {
      if (varietyLines.length === 0 || varietyLines.some((line) => !line.quantity)) {
        toast.error("Complete variety detail requires a quantity for every listed variety.");
        return;
      }
      if (currentVarietyTotal !== lot.currentQuantity) {
        toast.error(`Complete variety quantities must add up to the current lot total of ${number.format(lot.currentQuantity)}.`);
        return;
      }
    }
    setWorking(true);
    try {
      await services.nursery.updateLotVarieties(lot.id, {
        varietyDetailStatus: varietyStatus,
        varieties: varietyLines.map((line) => ({
          varietyId: line.varietyId,
          estimatedQuantity: line.quantity ? Number(line.quantity) : undefined,
          notes: line.notes.trim() || undefined,
        })),
      });
      toast.success("Variety detail updated", {
        description: varietyStatus === "COMPLETE"
          ? "The variety breakdown is reconciled to the living stock total."
          : "Nursery monitoring remains active even with incomplete variety detail.",
      });
      setSection(undefined);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Variety detail could not be updated.");
    } finally {
      setWorking(false);
    }
  };

  const addAssessment = async (event: FormEvent) => {
    event.preventDefault();
    if (!lot) return;
    const viable = Number(assessment.currentViableQuantity);
    const ready = Number(assessment.readyQuantity);
    if (!Number.isFinite(viable) || viable < 0 || !Number.isFinite(ready) || ready < 0 || ready > viable) {
      toast.error("Enter valid viable and ready seedling counts.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.addPopulationAssessment(lot.id, {
        assessedAt: new Date(assessment.assessedAt).toISOString(),
        currentViableQuantity: viable,
        readyQuantity: ready,
        mortalitySinceLast: assessment.mortalitySinceLast ? Number(assessment.mortalitySinceLast) : undefined,
        culledSinceLast: assessment.culledSinceLast ? Number(assessment.culledSinceLast) : undefined,
        varietyDetailStatus: assessment.varietyDetailStatus,
        notes: assessment.notes.trim() || undefined,
        assessedByEmployeeId: assessment.assessedByEmployeeId || undefined,
      });
      toast.success("Population assessment recorded", {
        description: `${number.format(viable)} viable seedlings; ${number.format(ready)} ready for distribution.`,
      });
      setSection(undefined);
      setAssessment({
        assessedAt: new Date().toISOString().slice(0, 16),
        currentViableQuantity: String(viable),
        readyQuantity: String(ready),
        mortalitySinceLast: "",
        culledSinceLast: "",
        varietyDetailStatus: assessment.varietyDetailStatus,
        notes: "",
        assessedByEmployeeId: assessment.assessedByEmployeeId,
      });
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Population assessment could not be recorded.");
    } finally {
      setWorking(false);
    }
  };

  const addActivity = async (event: FormEvent) => {
    event.preventDefault();
    if (!lot || !activity.activityId) {
      toast.error("Choose the nursery activity.");
      return;
    }
    const hasInput = !!activity.inputItemId;
    if (hasInput && (!activity.inventoryLocationId || !activity.inputQuantity || !activity.inputUnit.trim())) {
      toast.error("Input use requires the inventory location, quantity and unit.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.addActivity({
        clientGeneratedId: crypto.randomUUID(),
        nurseryId: lot.nurseryId,
        bedId: lot.bedId,
        lotId: lot.id,
        activityId: activity.activityId,
        performedAt: new Date(activity.performedAt).toISOString(),
        responsibleEmployeeId: activity.responsibleEmployeeId || undefined,
        notes: activity.notes.trim() || undefined,
        deviceIdentifier: "nursery-web",
        createdOffline: false,
        localCreatedAt: new Date(activity.performedAt).toISOString(),
        inputs: hasInput ? [{
          inputItemId: activity.inputItemId,
          inventoryLocationId: activity.inventoryLocationId,
          quantity: Number(activity.inputQuantity),
          unit: activity.inputUnit.trim(),
        }] : undefined,
      });
      toast.success("Nursery activity logged", {
        description: hasInput ? "The linked input was also issued from ordinary inventory." : undefined,
      });
      setSection(undefined);
      setActivity({
        activityId: "",
        performedAt: new Date().toISOString().slice(0, 16),
        responsibleEmployeeId: "",
        notes: "",
        inputItemId: "",
        inventoryLocationId: "",
        inputQuantity: "",
        inputUnit: "",
      });
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Nursery activity could not be recorded.");
    } finally {
      setWorking(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!lot) return <LoadingState label="Loading Seedling Lot" />;

  const survivalRate = lot.initialQuantity > 0 ? (lot.currentQuantity / lot.initialQuantity) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="nursery-lot-detail-page">
      <PageHeader
        eyebrow="Nursery Management · Living Stock"
        title={lot.lotCode}
        description={`${lot.nurseryName || "Nursery"} · ${lot.bedName || "Bed"} · ${lot.seedlingItemName || "Coffee seedlings"}`}
        backTo="/nursery"
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setSection("VARIETY")}><Layers3 className="mr-2 size-4" />Varieties</Button>
            <Button variant="outline" onClick={() => setSection("ACTIVITY")}><Activity className="mr-2 size-4" />Log Activity</Button>
            <Button onClick={() => setSection("ASSESSMENT")}><Plus className="mr-2 size-4" />Population Assessment</Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={lot.lifecycleStatus} testId="nursery-lot-lifecycle-status" />
        <span className="rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground">
          Variety detail: {lot.varietyDetailStatus.replace(/_/g, " ")}
        </span>
        <span className="text-sm text-muted-foreground">
          Source: {[lot.sourceName, lot.sourceLocation].filter(Boolean).join(" · ") || "Not recorded"}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Card><CardContent className="p-5"><Sprout className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(lot.initialQuantity)}</p><p className="text-sm text-muted-foreground">Initially established</p></CardContent></Card>
        <Card><CardContent className="p-5"><Sprout className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(lot.currentQuantity)}</p><p className="text-sm text-muted-foreground">Current viable</p></CardContent></Card>
        <Card><CardContent className="p-5"><Droplets className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(lot.readyQuantity)}</p><p className="text-sm text-muted-foreground">Ready for distribution</p></CardContent></Card>
        <Card><CardContent className="p-5"><TrendingDown className="size-5 text-warning" /><p className="mt-3 text-3xl font-semibold">{survivalRate.toFixed(1)}%</p><p className="text-sm text-muted-foreground">Current survival</p></CardContent></Card>
        <Card><CardContent className="p-5"><Activity className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{activities.length}</p><p className="text-sm text-muted-foreground">Activities logged</p></CardContent></Card>
      </div>

      {section === "ASSESSMENT" && (
        <Card className="border-primary/25">
          <CardHeader><CardTitle className="text-base">Record population / survival assessment</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={addAssessment} className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-2"><Label>Assessment time</Label><Input type="datetime-local" value={assessment.assessedAt} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, assessedAt: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Current viable seedlings</Label><Input type="number" min="0" value={assessment.currentViableQuantity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, currentViableQuantity: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Distribution-ready seedlings</Label><Input type="number" min="0" value={assessment.readyQuantity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, readyQuantity: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Mortality since last count</Label><Input type="number" min="0" value={assessment.mortalitySinceLast} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, mortalitySinceLast: e.target.value }))} placeholder="Optional" /></div>
              <div className="space-y-2"><Label>Culled since last count</Label><Input type="number" min="0" value={assessment.culledSinceLast} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, culledSinceLast: e.target.value }))} placeholder="Optional" /></div>
              <div className="space-y-2">
                <Label>Variety detail at this count</Label>
                <Select value={assessment.varietyDetailStatus} onValueChange={(value: VarietyDetailStatus) => setAssessment((v) => ({ ...v, varietyDetailStatus: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="NOT_RECORDED">Not recorded</SelectItem><SelectItem value="PARTIAL">Partial</SelectItem><SelectItem value="COMPLETE">Complete</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assessed by</Label>
                <Select value={assessment.assessedByEmployeeId} onValueChange={(value: string) => setAssessment((v) => ({ ...v, assessedByEmployeeId: value }))}>
                  <SelectTrigger><SelectValue placeholder="Optional staff member" /></SelectTrigger>
                  <SelectContent>{staff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2"><Label>Notes</Label><Textarea value={assessment.notes} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAssessment((v) => ({ ...v, notes: e.target.value }))} placeholder="Observations on survival, condition, hardening or readiness" /></div>
              <div className="md:col-span-2 xl:col-span-3 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setSection(undefined)}>Cancel</Button><Button disabled={working}>Save Assessment</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      {section === "ACTIVITY" && (
        <Card className="border-primary/25">
          <CardHeader><CardTitle className="text-base">Log Nursery Activity</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={addActivity} className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-2">
                <Label>Activity</Label>
                <Select value={activity.activityId} onValueChange={(value: string) => setActivity((v) => ({ ...v, activityId: value }))}>
                  <SelectTrigger><SelectValue placeholder="Choose activity" /></SelectTrigger>
                  <SelectContent>{activityCatalogue.map((item) => <SelectItem key={item.id} value={item.id}>{item.activityName}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Performed at</Label><Input type="datetime-local" value={activity.performedAt} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setActivity((v) => ({ ...v, performedAt: e.target.value }))} /></div>
              <div className="space-y-2">
                <Label>Responsible staff</Label>
                <Select value={activity.responsibleEmployeeId} onValueChange={(value: string) => setActivity((v) => ({ ...v, responsibleEmployeeId: value }))}>
                  <SelectTrigger><SelectValue placeholder="Optional staff member" /></SelectTrigger>
                  <SelectContent>{staff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2 xl:col-span-3"><Label>Activity notes</Label><Textarea value={activity.notes} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setActivity((v) => ({ ...v, notes: e.target.value }))} placeholder={selectedActivity?.description || "What was done or observed?"} /></div>
              <div className="md:col-span-2 xl:col-span-3 rounded-xl border bg-muted/20 p-4">
                <p className="font-medium">Optional inventory input used</p>
                <p className="mt-1 text-xs text-muted-foreground">Use this when fertilizer, fungicide, insecticide or another stocked input was consumed during this activity.</p>
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <Select value={activity.inputItemId} onValueChange={(value: string) => {
                    const item = inventoryItems.find((row) => row.id === value);
                    setActivity((v) => ({ ...v, inputItemId: value, inputUnit: item?.unit || v.inputUnit }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Input item (optional)" /></SelectTrigger>
                    <SelectContent>{inventoryItems.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={activity.inventoryLocationId} onValueChange={(value: string) => setActivity((v) => ({ ...v, inventoryLocationId: value }))}>
                    <SelectTrigger><SelectValue placeholder="Inventory location" /></SelectTrigger>
                    <SelectContent>{locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.locationName}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="0" step="0.0001" value={activity.inputQuantity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setActivity((v) => ({ ...v, inputQuantity: e.target.value }))} placeholder="Quantity" />
                  <Input value={activity.inputUnit} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setActivity((v) => ({ ...v, inputUnit: e.target.value }))} placeholder="Unit" />
                </div>
              </div>
              <div className="md:col-span-2 xl:col-span-3 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setSection(undefined)}>Cancel</Button><Button disabled={working}>Log Activity</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      {section === "VARIETY" && (
        <Card className="border-primary/25">
          <CardHeader><CardTitle className="text-base">Progressive variety classification</CardTitle><p className="text-sm text-muted-foreground">This information is optional. You can save partial or no quantity detail and continue all Nursery operations.</p></CardHeader>
          <CardContent>
            <form onSubmit={saveVarieties} className="space-y-4">
              <div className="max-w-sm space-y-2">
                <Label>Variety information status</Label>
                <Select value={varietyStatus} onValueChange={(value: VarietyDetailStatus) => setVarietyStatus(value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="NOT_RECORDED">Not recorded</SelectItem><SelectItem value="PARTIAL">Partial</SelectItem><SelectItem value="COMPLETE">Complete</SelectItem></SelectContent>
                </Select>
              </div>
              {varietyLines.map((line, index) => (
                <div key={index} className="grid gap-3 rounded-xl border p-4 md:grid-cols-[1.2fr_0.6fr_1.2fr_auto]">
                  <Select value={line.varietyId} onValueChange={(value: string) => setVarietyLines((current) => current.map((item, i) => i === index ? { ...item, varietyId: value } : item))}>
                    <SelectTrigger><SelectValue placeholder="Variety" /></SelectTrigger>
                    <SelectContent>{varieties.map((row) => <SelectItem key={row.id} value={row.id}>{row.varietyCode} · {row.varietyName}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="0" value={line.quantity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyLines((current) => current.map((item, i) => i === index ? { ...item, quantity: e.target.value } : item))} placeholder="Quantity optional" />
                  <Input value={line.notes} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyLines((current) => current.map((item, i) => i === index ? { ...item, notes: e.target.value } : item))} placeholder="Optional note" />
                  <Button type="button" variant="ghost" onClick={() => setVarietyLines((current) => current.filter((_, i) => i !== index))}>Remove</Button>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  Classified quantity: <span className="font-semibold text-foreground">{number.format(currentVarietyTotal)}</span> of {number.format(lot.currentQuantity)}
                  {varietyStatus !== "COMPLETE" && currentVarietyTotal < lot.currentQuantity && <span> · {number.format(lot.currentQuantity - currentVarietyTotal)} may remain unclassified</span>}
                </div>
                <Button type="button" variant="outline" onClick={addVarietyLine}><Plus className="mr-2 size-4" />Add Variety</Button>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setSection(undefined)}>Cancel</Button><Button disabled={working}>Save Variety Detail</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Population & survival history</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Viable</TableHead><TableHead>Ready</TableHead><TableHead>Mortality</TableHead><TableHead>Variety detail</TableHead></TableRow></TableHeader>
              <TableBody>
                {assessments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.assessedAt).toLocaleDateString()}</TableCell><TableCell className="font-semibold">{number.format(row.currentViableQuantity)}</TableCell><TableCell>{number.format(row.readyQuantity)}</TableCell><TableCell>{row.mortalitySinceLast === undefined ? "—" : number.format(row.mortalitySinceLast)}</TableCell><TableCell>{row.varietyDetailStatus.replace(/_/g, " ")}</TableCell>
                  </TableRow>
                ))}
                {assessments.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No population assessment yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Nursery activity timeline</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {activities.slice(0, 12).map((row) => (
              <div key={row.id} className="rounded-xl border p-4">
                <div className="flex items-start justify-between gap-4"><div><p className="font-semibold">{row.activityName || row.activityCode}</p><p className="text-xs text-muted-foreground">{new Date(row.performedAt).toLocaleString()}</p></div>{row.inputCount > 0 && <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">{row.inputCount} input{row.inputCount === 1 ? "" : "s"}</span>}</div>
                {row.notes && <p className="mt-2 text-sm text-muted-foreground">{row.notes}</p>}
              </div>
            ))}
            {activities.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No nursery activities logged yet.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
